import { defaultSupportedColumnTypes } from 'app/shared/utils/column-type.util';
import { EnvironmentInjector, Injectable } from '@angular/core';
import { IDatasetTypeMetadata, IRegisteredDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { A365WorkspaceCacheService } from 'app/services/metadata-client/A365workspace-cache.service';
import { IResourceTypeMetadata } from 'app/extensibility/metadatas/resource-type/resource-type-metadata';
import { ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { Column, GlobalVariables as shellGlobalVariables } from 'shell-models-lib';
import { getLogicalTypeByPhysicalType } from 'app/shared/utils/column-mapping.util';
import shellapi from 'a365-shell-api';

@Injectable({
    providedIn: 'root'
})
export class DatasetRegistryService {
    private readonly _datasetConfigurations: IRegisteredDatasetTypeMetadata[] = [];
    private readonly _metaDataCacheMap: Map<string, IRegisteredDatasetTypeMetadata> = new Map();

    constructor(private readonly _ecSvc: ExposureControlService, private readonly _injector: EnvironmentInjector) { }

    public getSupportedDataflowSourceModelNames(): string[] {
        return this._getDataflowSupportedDatasetConfigurations('source').map((c) => c.type);
    }

    public getSupportedDataflowSinkModelNames(): string[] {
        return this._getDataflowSupportedDatasetConfigurations('sink').map((c) => c.type);
    }

    public register(config: IRegisteredDatasetTypeMetadata): void {
        if (config.disabled) {
            return;
        }
        this._datasetConfigurations.push(config);
    }

    // eslint-disable-next-line
    public has(type: string): boolean {
        return this._datasetConfigurations.find((e) => e.type === type) != null;
    }

    // check if an alias dataset supports current linkedServiceType is existing
    public hasAliasDataset(linkedServiceType: string): boolean {
        return this._datasetConfigurations.find((e) => e.linkedServiceType === linkedServiceType) != null;
    }

    // check if a connector is under an EC and customer don't have access
    public hidedByEc(dataset: IDatasetTypeMetadata): boolean {
        if (GlobalVariables.showAllFeatures) {
            return false;
        }
        if (__IS_A365__) {
            const workspaceCache = this._injector.get(A365WorkspaceCacheService);
            if (
                (workspaceCache.isWorkspaceLite() || workspaceCache.isWorkspaceDisconnected()) &&
                dataset.type === adfModelTypeMap.sqlPool.datasetType
            ) {
                return true;
            }
        }
        return (
            (dataset.type === adfModelTypeMap.kusto.datasetType && !GlobalVariables.showKustoConnector) ||
            ((dataset.type === adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.datasetType ||
                dataset.type === adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.datasetType) &&
                !this._ecSvc.enableADLSCosmosDataset) ||
            (dataset.type === adfModelTypeMap.adobeExperiencePlatform.datasetType &&
                !GlobalVariables.enableAepInternal) ||
            (dataset.type === adfModelTypeMap.genericCData.datasetType &&
                !GlobalVariables.enableGenericCDataConnector) ||
            (dataset.type === adfModelTypeMap.delta.datasetType &&
                !GlobalVariables.supportDelta &&
                !GlobalVariables.supportDeltaCloneCopyJob) ||
            (dataset.linkedServiceType === adfModelTypeMap.sharePointOnlineFile.linkedServiceType &&
                !this._ecSvc.enableSharePointOnlineFile) ||
            (dataset.type === adfModelTypeMap.fabricSql.datasetType && !__IS_FABRIC_DPE__) ||
            (dataset.type === adfModelTypeMap.databricks.datasetType && !__IS_FABRIC_DPE__) ||
            (dataset.type === adfModelTypeMap.sqlAnalyticsEndpoint.datasetType && (!__IS_FABRIC_DPE__ || !shellGlobalVariables.enableSQLAnalyticsEndpoint))
        );
    }

    // all visible dataset types, used to be displayed in create dataset tiles, will contain alias dataset
    public getAllVisibleDatasetConfigurations(): IDatasetTypeMetadata[] {
        return this._datasetConfigurations
            .filter((e) => !e.notShowInTypeSelection && !this.hidedByEc(e))
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    }

    // primary (not alias) datasets and is not hided by EC, used in select existing dataset
    public getAllPrimaryAndAvailableDatasetConfigurations(): IDatasetTypeMetadata[] {
        return this._datasetConfigurations
            .filter((e) => !e.isAlias && !this.hidedByEc(e))
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    }

    public getAllFormatDataset(): IDatasetTypeMetadata[] {
        return this.getAllPrimaryAndAvailableDatasetConfigurations().filter((c) => c.isV2FormatDataset);
    }

    // all primary (not alias) datasets, will contain EC hided dataset
    public getAllPrimaryDatasetConfigurations(): IRegisteredDatasetTypeMetadata[] {
        return this._datasetConfigurations
            .filter((e) => !e.isAlias)
            .sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    }

    public getDatasetConfiguration(datasetType: string): IDatasetTypeMetadata {
        if (this._metaDataCacheMap.has(datasetType)) {
            return this._metaDataCacheMap.get(datasetType);
        }
        const metadatas = this._datasetConfigurations.filter((config) => config.type === datasetType);
        if (metadatas.length > 0) {
            const metadata = metadatas.find((config) => !config.isAlias) || metadatas[0];
            this._metaDataCacheMap.set(datasetType, metadata);
            return metadata;
        } else {
            throw new Error(`${datasetType} not found in dataset-registry`);
        }
    }

    public getVisibleDataflowSupportedDatasetConfigurations(
        type: 'source' | 'sink' = 'source'
    ): IDatasetTypeMetadata[] {
        return this.getAllVisibleDatasetConfigurations().filter((c) => this._supportDataflow(c, type));
    }

    public getDatasetSupportDataTypes(datasetType: string): string[] {
        const metadata = this.getDatasetConfiguration(datasetType);
        return metadata?.supportDataTypes || defaultSupportedColumnTypes;
    }

    public mapLogicalToPhysicalType(logicalType: string, datasetType: string, column?: Column | shellapi.queryAPI.IColumn, options?: { enableTimestampNtz?: boolean }): string {
        const metadata = this.getDatasetConfiguration(datasetType);
        if (metadata?.LogicalToPhysicalMapping) {
            return typeof metadata.LogicalToPhysicalMapping === 'function' ? metadata.LogicalToPhysicalMapping(column, options)?.[(logicalType || '').toLowerCase()]: metadata.LogicalToPhysicalMapping[(logicalType || '').toLowerCase()];
        }
        return logicalType;
    }

    public mapPhysicalToLogicalType(physicalType: string, datasetType: string, peerType?: string): string {
        const metadata = this.getDatasetConfiguration(datasetType);
        if (metadata?.PhysicalToLogicalMapping) {
            return getLogicalTypeByPhysicalType(metadata, (physicalType || '').toLowerCase(), peerType);
        }
        return physicalType;
    }

    /**
     * Get dataset metadata (not hidden by EC) based on linkedService type (or a specific datasetType)
     * @member {string} [linkedServiceType] linkedServiceType used to iterate all dataset metadata's supportLinkedServiceTypes
     * if more than one dataset are found, the one with 1-length supportLinkedServiceTypes will be preferentially used.
     * e.g. when linkedService is Teradata/SapHana ls, Teradata dataset/SAPHana dataset will be taken, instead of RelationTable dataset.
     * @member {string} [datasetType] the specific datasetType, if specified, use this datasetType to fetch dataset metadata
     * @member {string} [includeV2Dataset] if you need include v2 dataset metadata, e.g. DelimitedTextDataset, ParquetDataset. etc.
     */
    public getDatasetMetadataFromLsType(
        linkedServiceType: string,
        datasetType?: string,
        includeV2Dataset: boolean = false
    ): IDatasetTypeMetadata {
        if (datasetType) {
            return this.getDatasetConfiguration(datasetType);
        } else {
            const supportedDatasets = this.getAllPrimaryDatasetConfigurations().filter((metadata) => {
                return !includeV2Dataset
                    ? !this.hidedByEc(metadata) &&
                    !metadata.isV2FormatDataset &&
                    metadata.supportLinkedServiceTypes.some((t) => t === linkedServiceType)
                    : !this.hidedByEc(metadata) &&
                    metadata.supportLinkedServiceTypes.some((t) => t === linkedServiceType);
            });
            if (supportedDatasets && supportedDatasets.length > 0) {
                const datasetOnlySupportCurrentLs = supportedDatasets.find(
                    (dataset) => dataset.supportLinkedServiceTypes.length === 1
                );
                return datasetOnlySupportCurrentLs != null ? datasetOnlySupportCurrentLs : supportedDatasets[0];
            }
            return undefined;
        }
    }

    private _getDataflowSupportedDatasetConfigurations(type: 'source' | 'sink'): IDatasetTypeMetadata[] {
        return this.getAllPrimaryAndAvailableDatasetConfigurations().filter((c) => this._supportDataflow(c, type));
    }

    private _supportDataflow(c: IResourceTypeMetadata, type: 'source' | 'sink'): boolean {
        if (type === 'source') {
            return Boolean(
                typeof c.supportDataFlowSource === 'function' ? c.supportDataFlowSource() : c.supportDataFlowSource
            );
        }
        return Boolean(typeof c.supportDataFlowSink === 'function' ? c.supportDataFlowSink() : c.supportDataFlowSink);
    }
}
